import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Icon } from "@chakra-ui/react";
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Checkbox,
  Spinner,
} from "@chakra-ui/react";
import { Eye, EyeOff, Lock, Mail, User } from "lucide-react";
import { loginSchema, type LoginFormData } from "@/lib/validations/auth";
import { useAuthStore } from "@/store/auth";
import { useThemeColors } from "@/hooks/useThemeColors";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { settings: siteSettings } = useSiteSettings();

  // Theme-aware colors
  const colors = useThemeColors();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setLocalLoading(true);
    try {
      setError("");
      console.log("[LoginForm] Starting login for:", data.email);
      const loginData = {
        ...data,
        remember_me: rememberMe,
      };
      const result = await login(loginData);

      console.log("[LoginForm] Login result received:", result);
      console.log("[LoginForm] Result properties:", {
        success: result?.success,
        twoFactorSetupRequired: result?.twoFactorSetupRequired,
        twoFactorRequired: result?.twoFactorRequired,
        message: result?.message,
      });

      if (result.success) {
        console.log("[LoginForm] Login successful, redirecting to dashboard");
        navigate("/dashboard");
      } else if (result.twoFactorSetupRequired) {
        console.log(
          "[LoginForm] 2FA setup required, redirecting to setup page"
        );
        const redirectUrl = `/two-factor/setup?email=${encodeURIComponent(
          data.email
        )}&remember_me=${rememberMe ? "1" : "0"}`;
        console.log("[LoginForm] Redirect URL:", redirectUrl);
        window.location.href = redirectUrl;
      } else if (result.twoFactorRequired) {
        console.log(
          "[LoginForm] 2FA required, redirecting to verification page"
        );
        const redirectUrl = `/two-factor?email=${encodeURIComponent(
          data.email
        )}&remember_me=${rememberMe ? "1" : "0"}`;
        console.log("[LoginForm] Redirect URL:", redirectUrl);
        window.location.href = redirectUrl;
      } else {
        console.log("[LoginForm] Login failed, showing error:", result.message);
        setError(result.message || "Login failed");
        setLocalLoading(false);
      }
    } catch (err: any) {
      console.error("[LoginForm] Login error caught:", err);
      console.error("[LoginForm] Error response:", err.response);

      if (
        err.response?.status === 403 &&
        err.response?.data?.two_factor_setup_required
      ) {
        console.log("[LoginForm] 2FA setup required in error, redirecting");
        const redirectUrl = `/two-factor/setup?email=${encodeURIComponent(
          data.email
        )}&remember_me=${rememberMe ? "1" : "0"}`;
        window.location.href = redirectUrl;
        return;
      }

      if (err.response?.status === 429) {
        const lockoutMinutes = err.response?.data?.lockout_remaining_minutes;
        if (lockoutMinutes) {
          setError(
            `Too many login attempts. Please try again in ${lockoutMinutes} minute(s).`
          );
        } else {
          setError(
            err.response?.data?.errors?.[0] ||
              err.response?.data?.message ||
              "Too many login attempts. Please try again later."
          );
        }
      } else {
        setError(
          err.response?.data?.message ||
            err.response?.data?.errors?.[0] ||
            "Login failed"
        );
      }
      setLocalLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent={{ base: "center", lg: "flex-start" }}
      pl={{ base: 0, lg: 100 }}
      px={{ base: 4, sm: 6, lg: 0 }}
      bgImage="url('/assets/img/fbg.png')"
      bgPosition="center"
      bgRepeat="no-repeat"
      bgSize="cover"
    >
      <Card
        w="full"
        maxW="448px"
        ml={{ base: "0", md: "16px", lg: "120px" }}
        bg={colors.cardBg}
        borderRadius="20px"
        boxShadow="0 10px 30px rgba(0,0,0,0.06)"
      >
        <CardBody p={{ base: 6, sm: 8, md: 10 }}>
          <VStack spacing={{ base: 4, md: 5 }}>
            <Box textAlign="center" w="full">
              {/* Logo */}
              <Box
                mb={{ base: 2, md: 3 }}
                display="flex"
                justifyContent="center"
              >
                <img
                  src={siteSettings.auth_logo || "/assets/img/logo.jpg"}
                  alt={siteSettings.site_name || "Logo"}
                  style={{
                    maxWidth: "220px",
                    height: "66px",
                    width: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              {/* Sign In Header */}
              <Heading
                size={{ base: "sm", md: "md" }}
                color={colors.heading}
                mb={1}
                fontWeight="750"
              >
                Sign In
              </Heading>

              {/* Description */}
              <Text
                color="#6C7688"
                fontSize={{ base: "sm", sm: "sm" }}
                px={{ base: 2, sm: 0 }}
              >
                Please enter below details to access the dashboard
              </Text>
            </Box>

            {error && (
              <Alert
                status="error"
                borderRadius="md"
                fontSize={{ base: "xs", sm: "sm" }}
              >
                <AlertIcon />
                <Text fontSize={{ base: "xs", sm: "sm" }}>{error}</Text>
              </Alert>
            )}

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="100%">
              <VStack spacing={{ base: 3, md: 4 }} w="100%">
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel
                    fontSize={{ base: "xs", sm: "sm" }}
                    fontWeight="500"
                    color="gray.700"
                  >
                    Email Address
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      h={{ base: "40px", md: "48px" }}
                    >
                      <User size={14} />
                    </InputLeftElement>
                    <Input
                      {...register("email")}
                      type="email"
                      placeholder="Enter Email Address"
                      pl="10"
                      borderColor="#E7E8EB"
                      borderRadius="8px"
                      _hover={{ borderColor: "gray.400" }}
                      _focus={{
                        borderColor: "orange.500",
                        boxShadow: "0 0 0 1px #ed8936",
                      }}
                      fontSize={{ base: "xs", sm: "sm" }}
                      h={{ base: "40px", md: "48px" }}
                    />
                  </InputGroup>

                  <FormErrorMessage fontSize="xs">
                    {errors.email?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.password}>
                  <FormLabel
                    fontSize={{ base: "xs", sm: "sm" }}
                    fontWeight="500"
                    color="gray.700"
                  >
                    Password
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement
                      pointerEvents="none"
                      h={{ base: "40px", md: "48px" }}
                    >
                      <Icon
                        viewBox="0 0 24 24"
                        boxSize="14px"
                        stroke="#0A1B39"
                        fill="none"
                        strokeWidth="2"
                      >
                        <rect x="3" y="10" width="18" height="13" rx="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        <circle cx="12" cy="16" r="1.5" />
                      </Icon>
                    </InputLeftElement>

                    <Input
                      {...register("password")}
                      type={showPassword ? "text" : "password"}
                      placeholder="************"
                      pl="10"
                      borderColor="#E7E8EB"
                      borderRadius="8px"
                      _hover={{ borderColor: "gray.400" }}
                      _focus={{
                        borderColor: "orange.500",
                        boxShadow: "0 0 0 1px #ed8936",
                      }}
                      fontSize={{ base: "xs", sm: "sm" }}
                      h={{ base: "40px", md: "48px" }}
                    />
                    <InputRightElement
                      width="3rem"
                      h={{ base: "40px", md: "48px" }}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        p={0}
                        type="button"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        h="1.75rem"
                        minW="1.75rem"
                        _hover={{ bg: "transparent" }}
                      >
                        {showPassword ? (
                          <EyeOff size={14} color="#0A1B39" />
                        ) : (
                          <Eye size={14} color="#0A1B39" />
                        )}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage fontSize="xs">
                    {errors.password?.message}
                  </FormErrorMessage>
                </FormControl>

                <Box
                  mt={1}
                  w="100%"
                  display="flex"
                  flexDirection={{ base: "column", sm: "row" }}
                  gap={{ base: 2, sm: 3 }}
                  justifyContent="space-between"
                  alignItems={{ base: "flex-start", sm: "center" }}
                >
                  <Box>
                    <Checkbox
                      isChecked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      colorScheme="orange"
                      size="sm"
                    >
                      <Text
                        fontSize={{ base: "xs", sm: "sm" }}
                        color={colors.link}
                      >
                        Remember me
                      </Text>
                    </Checkbox>
                  </Box>
                  <Box>
                    <Button
                      variant="link"
                      color="#B1B1B1"
                      fontSize={{ base: "xs", sm: "sm" }}
                      fontWeight={400}
                      onClick={() => navigate("/forgot-password")}
                      _hover={{
                        color: "orange.600",
                        textDecoration: "underline",
                      }}
                      p={0}
                      h="auto"
                      minW="auto"
                    >
                      Forgot your password?
                    </Button>
                  </Box>
                </Box>

                <Button
                  type="submit"
                  w="full"
                  borderRadius="10px"
                  background="linear-gradient(90deg, #dc3611 0%, #fa886f 100%)"
                  color="white"
                  size={{ base: "md", md: "lg" }}
                  fontSize={{ base: "sm", md: "md" }}
                  fontWeight="600"
                  _hover={{
                    background:
                      "linear-gradient(90deg, #c92f0e 0%, #f97960 100%)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(220, 54, 17, 0.3)",
                  }}
                  _active={{
                    background:
                      "linear-gradient(90deg, #b32a0d 0%, #f86951 100%)",
                    transform: "translateY(0)",
                  }}
                  isDisabled={localLoading}
                  transition="all 0.2s"
                >
                  {localLoading ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={2}
                    >
                      <Spinner size="sm" color="white" thickness="2px" />
                      <Text>Signing in...</Text>
                    </Box>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>
      <Text
        fontSize={{ base: "14px", sm: "14px", md: "14px" }}
        color="gray.900"
        position="fixed"
        bottom={{ base: "20px", sm: "80px", md: "80px" }}
        fontWeight="400"
        left={{ base: "50%", lg: "244px" }}
        transform={{ base: "translateX(-50%)", lg: "none" }}
        textAlign={{ base: "center", lg: "left" }}
        px={{ base: 4, lg: 0 }}
      >
        Copyright ©2025 – ActionLabs
      </Text>
    </Box>
  );
}
